/**
 * MediaRepository — reads and writes the media table. SQL only (rule R3).
 *
 * Every query that pages or seeks uses MEDIA_ORDER, so a photo's position in the
 * filmstrip is the same number no matter which query asked for it.
 */

import type { SqliteClient } from '../adapters/sqlite-client';
import { MEDIA_ORDER } from '../infra/schema';
import { toSql } from '../domain/filter-predicate';
import type { ListMediaOpts, MediaFilter, MediaItem, MediaStatus, Page } from '../types';
import { toMedia } from './row-mapping';

export interface NewMediaRow {
  relPath: string;
  filename: string;
  type: 'image' | 'video';
  ext: string;
  sizeBytes: number | null;
  modifiedAt: number | null;
}

export class MediaRepository {
  constructor(private readonly db: SqliteClient) {}

  /** Idempotent: re-scanning a folder adds only files not already registered. */
  insertMany(projectId: number, sourceId: number, rows: NewMediaRow[], now: number): number {
    if (rows.length === 0) return 0;

    return this.db.transaction(() => {
      let added = 0;
      for (const row of rows) {
        const result = this.db.run(
          `INSERT OR IGNORE INTO media
             (project_id, source_id, rel_path, filename, type, ext, size_bytes, modified_at, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            projectId,
            sourceId,
            row.relPath,
            row.filename,
            row.type,
            row.ext,
            row.sizeBytes,
            row.modifiedAt,
            now,
          ],
        );
        added += result.changes;
      }
      return added;
    });
  }

  list(projectId: number, opts: ListMediaOpts): Page<MediaItem> {
    const { where, params } = toSql(projectId, opts.filter);

    const total = Number(this.db.scalar(`SELECT COUNT(*) FROM media WHERE ${where}`, params) ?? 0);
    const items = this.db
      .all(`SELECT * FROM media WHERE ${where} ORDER BY ${MEDIA_ORDER} LIMIT ? OFFSET ?`, [
        ...params,
        opts.limit,
        opts.offset,
      ])
      .map(toMedia);

    return { items, total, offset: opts.offset, limit: opts.limit };
  }

  /** Every id in the filtered order — the filmstrip's spine. */
  idsFor(projectId: number, filter: MediaFilter): number[] {
    const { where, params } = toSql(projectId, filter);
    return this.db
      .all<{ id: number }>(`SELECT id FROM media WHERE ${where} ORDER BY ${MEDIA_ORDER}`, params)
      .map((row) => Number(row.id));
  }

  byId(id: number): MediaItem | null {
    const row = this.db.get('SELECT * FROM media WHERE id = ?', [id]);
    return row ? toMedia(row) : null;
  }

  byIds(ids: number[]): MediaItem[] {
    if (ids.length === 0) return [];
    const holes = ids.map(() => '?').join(',');
    return this.db
      .all(`SELECT * FROM media WHERE id IN (${holes}) ORDER BY ${MEDIA_ORDER}`, ids)
      .map(toMedia);
  }

  /** Zero-based position of `mediaId` within the filtered ordering, or -1. */
  indexOf(projectId: number, mediaId: number, filter: MediaFilter): number {
    const target = this.byId(mediaId);
    if (!target || target.projectId !== projectId) return -1;

    const { where, params } = toSql(projectId, filter);
    const before = this.db.scalar(
      `SELECT COUNT(*) FROM media
        WHERE ${where}
          AND (rel_path COLLATE NOCASE < ? OR (rel_path COLLATE NOCASE = ? AND id < ?))`,
      [...params, target.relPath, target.relPath, mediaId],
    );
    return Number(before ?? 0);
  }

  /** Everything assigned to any of these buckets, in filmstrip order. */
  forBuckets(projectId: number, bucketIds: number[]): MediaItem[] {
    if (bucketIds.length === 0) return [];
    const holes = bucketIds.map(() => '?').join(',');
    return this.db
      .all(
        `SELECT * FROM media
          WHERE project_id = ? AND bucket_id IN (${holes})
          ORDER BY ${MEDIA_ORDER}`,
        [projectId, ...bucketIds],
      )
      .map(toMedia);
  }

  setBucket(id: number, bucketId: number | null): void {
    this.db.run('UPDATE media SET bucket_id = ? WHERE id = ?', [bucketId, id]);
  }

  setRotation(id: number, rotation: number): void {
    const normalised = ((Math.round(rotation / 90) * 90) % 360 + 360) % 360;
    this.db.run('UPDATE media SET rotation = ? WHERE id = ?', [normalised, id]);
  }

  setThumbKey(id: number, key: string | null): void {
    this.db.run('UPDATE media SET thumb_key = ? WHERE id = ?', [key, id]);
  }

  setPreviewKey(id: number, key: string | null): void {
    this.db.run('UPDATE media SET preview_key = ? WHERE id = ?', [key, id]);
  }

  setDimensions(id: number, width: number, height: number): void {
    this.db.run('UPDATE media SET width = ?, height = ? WHERE id = ?', [width, height, id]);
  }

  setStatus(id: number, status: MediaStatus, checkedAt: number): void {
    this.db.run('UPDATE media SET status = ?, status_checked_at = ? WHERE id = ?', [
      status,
      checkedAt,
      id,
    ]);
  }

  /** Ids of photos with no thumbnail yet, oldest first — the thumbnail queue. */
  missingThumbs(projectId: number, limit: number): MediaItem[] {
    return this.db
      .all(
        `SELECT * FROM media
          WHERE project_id = ? AND thumb_key IS NULL AND status = 'ok' AND type = 'image'
          ORDER BY ${MEDIA_ORDER}
          LIMIT ?`,
        [projectId, limit],
      )
      .map(toMedia);
  }

  countsByBucket(projectId: number): Map<number, number> {
    const rows = this.db.all<{ bucket_id: number; n: number }>(
      `SELECT bucket_id, COUNT(*) AS n FROM media
        WHERE project_id = ? AND bucket_id IS NOT NULL
        GROUP BY bucket_id`,
      [projectId],
    );
    return new Map(rows.map((r) => [Number(r.bucket_id), Number(r.n)]));
  }

  countAll(projectId: number): number {
    return Number(this.db.scalar('SELECT COUNT(*) FROM media WHERE project_id = ?', [projectId]) ?? 0);
  }

  /** Cache keys still referenced by a row — anything else in OPFS is an orphan. */
  liveCacheKeys(): Set<string> {
    const rows = this.db.all<{ k: string }>(
      `SELECT thumb_key AS k FROM media WHERE thumb_key IS NOT NULL
       UNION SELECT preview_key AS k FROM media WHERE preview_key IS NOT NULL`,
    );
    return new Set(rows.map((r) => String(r.k)));
  }
}
